// preview-strip — teacher-preview-split (tasks 9.3 / 9.4 — R3 / R17 / R24),
// teacher-preview-cleanup (findings 2, 3 and 6).
//
// The compact "Preview — nothing is saved" indicator, with its exit control, for the SEVEN
// isolated editor surfaces (predict, debug, completion, tweak, guided-make, assignment,
// challenge). The D8 banner only exists inside `pc-classroom`, so without this a previewing
// teacher would spend most of the preview on pages with no indicator and no way back.
//
// SECURITY (R17): this renders ONLY when the SERVER confirmed preview mode — either
// `cfg.preview` (resolved against `isCourseStaff` by the `helpers.previewMode` pre-handler
// before the handler builds `cfg`) or `Snippet.isPreview` threaded through
// `assignmentSurfaceData` for the two project-keyed embeds. It can therefore never appear
// over genuinely persisting work, which on guided-make in particular would sit over real
// Draft edits while the completion tick never fired.
//
// Host chrome ONLY: no runner, no editor-core contract, no COOP/COEP.

var STYLES = [
  '.pcpv-strip{',
  '  display:flex;align-items:center;gap:10px;',
  '  padding:6px 14px;',
  '  background:var(--color-accent-soft);',
  '  color:var(--ink-800);',
  '  border-bottom:1px solid var(--color-border-strong);',
  '  font-family:var(--font-sans,system-ui,sans-serif);font-size:var(--fs-sm,0.875rem);font-weight:600;',
  '}',
  '.pcpv-strip-text{flex:1;min-width:0;}',
  '.pcpv-strip button{',
  '  flex:0 0 auto;min-height:30px;padding:0 14px;',
  '  border:1px solid var(--color-border-strong);border-radius:var(--radius-pill);',
  '  background:var(--color-surface);color:var(--ink-800);',
  '  font:inherit;font-weight:700;cursor:pointer;',
  '}',
  '.pcpv-strip button:hover{background:var(--sand-50);}',
  // `--focus-ring` is a COMPLETE box-shadow value, not a colour: `0 0 0 3px var(--focus-ring)`
  // parses as an invalid shadow and silently deletes the ring.
  '.pcpv-strip button:focus-visible{outline:none;box-shadow:var(--focus-ring);}'
].join('\n');

function ensureStyles(doc) {
  if (doc.getElementById('pcpv-strip-styles')) { return; }
  var el = doc.createElement('style');
  el.id = 'pcpv-strip-styles';
  el.textContent = STYLES;
  (doc.head || doc.documentElement).appendChild(el);
}

/** Drop the preview flag from this surface's own URL and reload. */
function reloadWithoutPreview() {
  try {
    var url = new URL(window.location.href);
    url.searchParams.delete('preview');
    window.location.href = url.toString();
  } catch (e) {
    window.location.reload();
  }
}

// C0 controls plus DEL. Browsers STRIP tab/CR/LF before parsing a URL, so `/<TAB>/evil.com`
// would pass a naive prefix check and then resolve protocol-relative. Written as a codepoint
// scan rather than a character class so the check needs no lint waiver.
function hasControlChar(value) {
  for (var i = 0; i < value.length; i++) {
    var code = value.charCodeAt(i);
    if (code < 0x20 || code === 0x7F) { return true; }
  }
  return false;
}

/**
 * teacher-preview-cleanup (finding 2 / A4): accept a client-supplied exit target ONLY when it
 * is an unambiguous same-origin ABSOLUTE PATH.
 *
 * The value reaches us from the classroom via a query parameter, so it must be validated even
 * though all three routes are owner- or staff-gated. A naive "starts with / and not //" check
 * has two live bypasses: `/\evil.com` (the WHATWG URL parser normalises `\` to `/`, giving a
 * protocol-relative URL) and the control-character case above. Both are rejected before the
 * prefix test, and the parsed origin is then compared.
 *
 * @return {String|null} the safe path, or null.
 */
function safeExitTarget(value) {
  if (typeof value !== 'string' || !value) { return null; }
  // (a) no backslash, no C0 control character, no surrounding whitespace.
  if (value !== value.trim()) { return null; }
  if (value.indexOf('\\') !== -1) { return null; }
  if (hasControlChar(value)) { return null; }
  // (b) a single leading slash.
  if (value.charAt(0) !== '/' || value.charAt(1) === '/') { return null; }
  // (c) it must genuinely resolve to this origin.
  try {
    if (new URL(value, window.location.origin).origin !== window.location.origin) { return null; }
  } catch (e) {
    return null;
  }
  return value;
}

/**
 * Mount the strip at the top of the page.
 *
 * @param {Document} doc
 * @param {Object}   opts
 * @param {Boolean}  opts.active   server-confirmed preview mode. Anything falsy is a no-op.
 * @param {String}  [opts.exit]    'reload' (default) or 'navigate'.
 *
 *   'reload'   — the four `window.open(..., 'noopener')` surfaces (predict, debug, completion,
 *                tweak). Their preview state IS the `?preview=` query flag, so dropping it and
 *                reloading genuinely leaves preview and keeps the teacher on the activity.
 *   'navigate' — the assignment embed, the challenge embed and guided-make: the three surfaces
 *                reached by a SAME-TAB navigation from the classroom (R24/R25). The first two
 *                take their preview state from `Snippet.isPreview`, so a reload-without-flag is
 *                a dead control; and `history.back()` — what these used to do — returns to the
 *                classroom URL STILL carrying `?preview=student`, dropping the teacher straight
 *                back into preview. They navigate to an explicit, validated exit target instead.
 *
 * @param {String}  [opts.exitUrl] the candidate exit target for 'navigate' (client-supplied;
 *                                 validated by `safeExitTarget` before it is used).
 * @param {Boolean} [opts.reloadFallback] with 'navigate', when no VALID exitUrl is present:
 *                  true  — fall back to 'reload' (guided-make ONLY: its flag really is the
 *                          `?preview=` param, so reloading works there);
 *                  false — render the strip with its text and NO exit button. A missing
 *                          control is honest; a dead one teaches teachers the feature is
 *                          broken (A5).
 * @return {Element|null} the mounted strip.
 */
export function mountPreviewStrip(doc, opts) {
  var o = opts || {};
  if (!o.active) { return null; }
  ensureStyles(doc);

  var navigate = o.exit === 'navigate';
  var target   = navigate ? safeExitTarget(o.exitUrl) : null;
  var canExit  = !navigate || !!target || o.reloadFallback === true;

  var strip = doc.createElement('div');
  strip.className = 'pcpv-strip';
  strip.setAttribute('role', 'status');

  var text = doc.createElement('span');
  text.className = 'pcpv-strip-text';
  text.textContent = 'Preview — nothing is saved';
  strip.appendChild(text);

  if (canExit) {
    var leave = doc.createElement('button');
    leave.type = 'button';
    // Only the surfaces that genuinely navigate AWAY say so; everything else keeps R3's label.
    leave.textContent = target ? 'Back to class' : 'Leave preview';
    leave.addEventListener('click', function () {
      if (target) {
        // `replace`, not `href`: Back must not walk straight back into the preview surface.
        window.location.replace(target);
        return;
      }
      reloadWithoutPreview();
    });
    strip.appendChild(leave);
  }

  var body = doc.body;
  if (body) { body.insertBefore(strip, body.firstChild); }
  return strip;
}

/**
 * teacher-preview-split (task 9.5 / R10 / R24): DISABLE a control in preview mode with
 * explanatory copy, rather than hiding it. Hiding the assignment Turn-in control would leave
 * a previewing teacher at a dead end in the "In progress / Continue" state with no indication
 * why the flow stops; clicking it un-disabled gives a bare failure (`submitAssignment` blocks
 * an `isPreview` parent server-side).
 *
 * teacher-preview-cleanup (finding 3 / A19 / A20): this NO LONGER writes the explanation into
 * a status element. It used to write it into `#assignment-save-status`, which autosave's
 * `setStatus()` overwrites on the very first keystroke — so the reason vanished and only the
 * `title` survived, on a control that `disabled` removes from the tab order. The explanation is
 * now SERVER-RENDERED persistent text beside the button, which a screen-reader user reaches in
 * normal document order. `disabled` stays (A19): the surface attaches no click handler in
 * preview, so `aria-disabled`-only would change real behaviour.
 */
export function disableForPreview(btn, message) {
  if (!btn) { return; }
  btn.disabled = true;
  btn.setAttribute('aria-disabled', 'true');
  btn.title = message;
}
